import { Extension } from '@tiptap/core';
import { parseStyleString, responsiveVarName, stringifyStyleObject } from '@/plugins/tiptap-v3/functionality/responsive/responsive';
function getBreakpoint(editor) {
    var _a, _b;
    return ((_b = (_a = editor === null || editor === void 0 ? void 0 : editor.storage) === null || _a === void 0 ? void 0 : _a.responsiveEngine) === null || _b === void 0 ? void 0 : _b.breakpoint) || 'lg';
}
function getResponsiveConfig(options) {
    if (!options.responsive || !options.responsive.enabled)
        return null;
    return {
        prefix: options.responsive.prefix || 'vp',
        cssPropTextAlign: options.responsive.cssPropTextAlign || 'text-align',
    };
}
function getConfiguredBreakpoints(editor) {
    var _a, _b, _c;
    const responsiveEngine = (_b = (_a = editor === null || editor === void 0 ? void 0 : editor.extensionManager) === null || _a === void 0 ? void 0 : _a.extensions) === null || _b === void 0 ? void 0 : _b.find((ext) => ext.name === 'responsiveEngine');
    const bps = (_c = responsiveEngine === null || responsiveEngine === void 0 ? void 0 : responsiveEngine.options) === null || _c === void 0 ? void 0 : _c.breakpoints;
    if (Array.isArray(bps) && bps.length) {
        return bps;
    }
    return ['lg', 'md', 'sm'];
}
function getResponsiveStyleValue(style, cssProp, prefix, breakpoint, breakpoints) {
    const idx = breakpoints.indexOf(breakpoint);
    if (idx === -1)
        return null;
    const styleObj = parseStyleString(style || '');
    for (let i = idx; i >= 0; i--) {
        const varName = responsiveVarName(cssProp, breakpoints[i], prefix);
        const value = styleObj[varName];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }
    return null;
}
/**
 * This extension allows you to align text.
 * @see https://www.tiptap.dev/api/extensions/text-align
 */
export const TextAlign = Extension.create({
    name: 'textAlign',
    addOptions() {
        return {
            types: [],
            alignments: ['left', 'center', 'right', 'justify'],
            defaultAlignment: null,
            responsive: {
                enabled: true,
                prefix: 'vp',
                cssPropTextAlign: 'text-align',
            },
        };
    },
    addGlobalAttributes() {
        const cfg = getResponsiveConfig(this.options);
        const prefix = (cfg === null || cfg === void 0 ? void 0 : cfg.prefix) || 'vp';
        const varPrefix = `--${prefix}-`;
        return [
            {
                types: this.options.types,
                attributes: {
                    style: {
                        default: null,
                        parseHTML: element => {
                            const styleAttr = element.getAttribute('style') || '';
                            if (!styleAttr || !cfg)
                                return null;
                            const styleObj = parseStyleString(styleAttr);
                            const responsiveVars = {};
                            Object.entries(styleObj).forEach(([key, value]) => {
                                if (key.startsWith(varPrefix)) {
                                    responsiveVars[key] = value;
                                }
                            });
                            const nextStyle = stringifyStyleObject(responsiveVars);
                            return nextStyle || null;
                        },
                        renderHTML: attributes => {
                            if (!attributes.style || !cfg) {
                                return {};
                            }
                            return {
                                style: attributes.style,
                            };
                        },
                    },
                    textAlign: {
                        default: this.options.defaultAlignment,
                        parseHTML: element => {
                            const alignment = element.style.textAlign;
                            return this.options.alignments.includes(alignment) ? alignment : this.options.defaultAlignment;
                        },
                        renderHTML: attributes => {
                            if (!attributes.textAlign) {
                                return {};
                            }
                            // In responsive mode we keep text-align as CSS vars on `style`.
                            // Avoid emitting a fixed inline text-align from this attribute.
                            if (cfg) {
                                return {};
                            }
                            return { style: `text-align: ${attributes.textAlign}` };
                        },
                    },
                },
            },
        ];
    },
    addCommands() {
        return {
            setTextAlign: (alignment, opts) => ({ editor, commands }) => {
                if (!this.options.alignments.includes(alignment)) {
                    return false;
                }
                const cfg = getResponsiveConfig(this.options);
                if (!cfg) {
                    return this.options.types
                        .map(type => commands.updateAttributes(type, { textAlign: alignment }))
                        .some(response => response);
                }
                const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                const taVar = responsiveVarName(cfg.cssPropTextAlign, bp, cfg.prefix);
                return this.options.types
                    .map(type => {
                    const attrs = editor.getAttributes(type) || {};
                    const styleObj = parseStyleString(attrs.style || '');
                    // Legacy compatibility: remove fixed text-align so responsive vars are authoritative.
                    delete styleObj['text-align'];
                    styleObj[taVar] = alignment;
                    return commands.updateAttributes(type, {
                        ...attrs,
                        style: stringifyStyleObject(styleObj),
                        textAlign: null,
                    });
                })
                    .some(response => response);
            },
            unsetTextAlign: (opts) => ({ editor, commands }) => {
                const cfg = getResponsiveConfig(this.options);
                if (!cfg) {
                    return this.options.types.map(type => commands.resetAttributes(type, 'textAlign')).some(response => response);
                }
                const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                const taVar = responsiveVarName(cfg.cssPropTextAlign, bp, cfg.prefix);
                return this.options.types
                    .map(type => {
                    const attrs = editor.getAttributes(type) || {};
                    const styleObj = parseStyleString(attrs.style || '');
                    delete styleObj['text-align'];
                    delete styleObj[taVar];
                    return commands.updateAttributes(type, {
                        ...attrs,
                        style: stringifyStyleObject(styleObj),
                        textAlign: null,
                    });
                })
                    .some(response => response);
            },
            toggleTextAlign: (alignment, opts) => ({ editor, commands }) => {
                if (!this.options.alignments.includes(alignment)) {
                    return false;
                }
                const cfg = getResponsiveConfig(this.options);
                if (!cfg) {
                    if (editor.isActive({ textAlign: alignment })) {
                        return commands.unsetTextAlign();
                    }
                    return commands.setTextAlign(alignment);
                }
                const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                const breakpoints = getConfiguredBreakpoints(editor);
                const currentAlign = this.options.types
                    .map(type => {
                    const attrs = editor.getAttributes(type) || {};
                    return getResponsiveStyleValue(attrs.style || '', cfg.cssPropTextAlign, cfg.prefix, bp, breakpoints);
                })
                    .find(Boolean);
                if (currentAlign === alignment) {
                    return commands.unsetTextAlign(opts);
                }
                return commands.setTextAlign(alignment, opts);
            },
        };
    },
    addKeyboardShortcuts() {
        return {
            'Mod-Shift-l': () => this.editor.commands.setTextAlign('left'),
            'Mod-Shift-e': () => this.editor.commands.setTextAlign('center'),
            'Mod-Shift-r': () => this.editor.commands.setTextAlign('right'),
            'Mod-Shift-j': () => this.editor.commands.setTextAlign('justify'),
        };
    },
});
